import { useState, useEffect } from 'react';
import { Trash2, Upload, Search, RefreshCw, AlertTriangle, Check, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminImages = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const token = localStorage.getItem('chaleur_admin_token');

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/upload/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data.images || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await axios.post(`${API}/upload/image`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        successCount++;
      } catch (error) {
        console.error('Upload error:', error);
        failCount++;
      }
    }

    setUploading(false);
    
    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} image(s)`);
      fetchImages();
    }
    if (failCount > 0) {
      toast.error(`Failed to upload ${failCount} image(s)`);
    }
    
    e.target.value = '';
  };

  const handleDelete = async (filename) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/upload/image/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image deleted');
      setImages(prev => prev.filter(img => img.filename !== filename));
      setDeleteConfirm(null);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete image';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const filteredImages = images.filter(img =>
    img.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8" data-testid="admin-images">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-light mb-2">Image Library</h1>
          <p className="text-muted-foreground">{images.length} images in library</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchImages} className="rounded-none">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <label>
            <Button className="rounded-none cursor-pointer" disabled={uploading}>
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Images'}
            </Button>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              multiple
              onChange={handleUpload}
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search images..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-none"
        />
      </div>

      {/* Image Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="aspect-square bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : filteredImages.length === 0 ? (
        <Card className="rounded-none">
          <CardContent className="py-12 text-center">
            <ImageIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchTerm ? 'No images match your search' : 'No images in library. Upload your first image!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredImages.map((image) => (
            <div
              key={image.filename}
              className="group relative aspect-square bg-gray-100 overflow-hidden border border-gray-200"
            >
              <img
                src={image.url}
                alt={image.filename}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
              />
              
              {/* Overlay with delete button */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                <Button
                  variant="destructive"
                  size="sm"
                  className="opacity-0 group-hover:opacity-100 transition-opacity rounded-none"
                  onClick={() => setDeleteConfirm(image.filename)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Filename */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                {image.filename}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirm(null)} />
          <Card className="relative rounded-none w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Confirm Delete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete this image? This action cannot be undone.
              </p>
              <div className="aspect-video bg-gray-100 overflow-hidden">
                <img
                  src={images.find(i => i.filename === deleteConfirm)?.url}
                  alt="To be deleted"
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-xs font-mono bg-gray-100 p-2 truncate">{deleteConfirm}</p>
              <p className="text-sm text-amber-600">
                Note: Images used by products cannot be deleted.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirm(null)}
                  className="rounded-none"
                  disabled={deleting}
                >
                  <X className="w-4 h-4 mr-2" /> Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(deleteConfirm)}
                  className="rounded-none"
                  disabled={deleting}
                >
                  {deleting ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminImages;
